import config from 'config';
import FlRequest from 'modules/request';
import { SmartSuggestV4 } from '../../../declarations/request';
import LayoutAssistedSuggestHooks from 'LayoutAssistedSuggestHooks';
import searchForm from 'modules/search-form';

type RequestParameters = Record<string, any>;

const SUPPORTED_API_VERSIONS = ['result_v4', 'result_v5'];

export default class RequestService {
    private lastUsedQuery: string;
    private lastUsedQueryWhichCompleted: string;
    private lastResponse: SmartSuggestV4.Response;
    private mostRecentTopQueries: SmartSuggestV4.TextSuggestion[] = [];
    private mostRecentResponseWithProducts: SmartSuggestV4.Response;
    private readonly resultSubscribers: ((result: SmartSuggestV4.Response) => void)[] = [];
    private readonly resultWithFallbackSubscribers: ((
        result: SmartSuggestV4.Response,
        original: SmartSuggestV4.Response | null
    ) => void)[] = [];
    private readonly activeRequests: Map<string, Record<string, any>> = new Map();

    private updateSubscribers(response: SmartSuggestV4.Response): void {
        this.lastUsedQueryWhichCompleted = this.lastUsedQuery;
        response = this.sanitizeResponse(response);
        response = LayoutAssistedSuggestHooks.beforeUpdate(response);
        this.updateLastResponse(response);

        this.resultSubscribers.forEach((sub: (result: SmartSuggestV4.Response) => void) => sub(response));

        const fallbackResponse = this.createFallbackFromResponse(response);
        this.resultWithFallbackSubscribers.forEach(
            (sub: (result: SmartSuggestV4.Response, original: SmartSuggestV4.Response | null) => void) => sub(fallbackResponse, response)
        );
    }

    private updateLastResponse(response: SmartSuggestV4.Response): void {
        this.lastResponse = response;

        if (response.topQueries?.length > 0) {
            this.mostRecentTopQueries = response.topQueries;
        }

        if (response.products?.length > 0) {
            this.mostRecentResponseWithProducts = response;
        }
    }

    private sanitizeResponse(response: SmartSuggestV4.Response): SmartSuggestV4.Response {
        const hugoNoCountFoundFlag = -1;
        if (response?.totalResults === hugoNoCountFoundFlag) {
            // If hugo did not find any count then just set it to zero
            response.totalResults = 0;
        }

        return response;
    }

    private singleRequest(
        requestType: string,
        requestObject: RequestParameters,
        callback: (results: SmartSuggestV4.Response) => void
    ): void {
        const request = new FlRequest({
            onSuccess: callback,
        });

        request.send(requestType, requestObject);
    }

    private responseHasNonEmptyBlocks(response: SmartSuggestV4.Response): boolean {
        return (
            response?.textSuggestions?.length > 0 ||
            !!response?.landingpage ||
            !!response?.attributeSuggestions?.cat ||
            !!response?.attributeSuggestions?.vendor
        );
    }

    private createFallbackFromResponse(response: SmartSuggestV4.Response): SmartSuggestV4.Response {
        const responseClone = JSON.parse(JSON.stringify(response));
        const fallbackTopQueries = this.responseHasNonEmptyBlocks(responseClone) ? [] : this.mostRecentTopQueries;
        responseClone.topQueries = responseClone?.topQueries?.length > 0 ? responseClone.topQueries : fallbackTopQueries;

        const fallbackToMostRecentProducts =
            config.assistedSuggest.config.showResultsIfLastSearchHadNoResults && responseClone?.products?.length === 0;
        responseClone.products = !fallbackToMostRecentProducts
            ? responseClone.products
            : this.mostRecentResponseWithProducts?.products || [];

        return responseClone;
    }

    private addOldPriceProperty(queryParams: RequestParameters): RequestParameters {
        if (config.assistedSuggest.config.oldPriceProperty) {
            if (Array.isArray(queryParams.properties) && !queryParams.properties.includes(config.assistedSuggest.config.oldPriceProperty)) {
                queryParams.properties.push(config.assistedSuggest.config.oldPriceProperty);
            } else if (!queryParams.properties) {
                queryParams.properties = [config.assistedSuggest.config.oldPriceProperty];
            }
        }

        return queryParams;
    }

    private addEmptyQuerySort(query: string, queryParams: RequestParameters): RequestParameters {
        if (!query.trim() && config.assistedSuggest.config.emptyQuerySort) {
            queryParams.order = config.assistedSuggest.config.emptyQuerySort;
        }

        return queryParams;
    }

    private addEmptyQueryFilter(query: string, queryParams: RequestParameters): RequestParameters {
        if (!query.trim() && config.assistedSuggest.config.emptyQueryFilterName && config.assistedSuggest.config.emptyQueryFilterValue) {
            queryParams.attrib = {
                ...(queryParams.attrib || {}),
                [config.assistedSuggest.config.emptyQueryFilterName]: [config.assistedSuggest.config.emptyQueryFilterValue],
            };
        }

        return queryParams;
    }

    public getMostRecentTopQueries(): SmartSuggestV4.TextSuggestion[] {
        return this.mostRecentTopQueries;
    }

    public getMostRecentResponseWithProducts(): SmartSuggestV4.Response {
        return this.mostRecentResponseWithProducts;
    }

    public getLastResponse(): SmartSuggestV4.Response {
        return this.lastResponse;
    }

    public onResultChange(callback: (result: SmartSuggestV4.Response) => void): void {
        this.resultSubscribers.push(callback);

        if (this.lastResponse) {
            callback(this.lastResponse);
        }
    }

    public onResultChangeWithFallback(callback: (result: SmartSuggestV4.Response, original: SmartSuggestV4.Response | null) => void): void {
        this.resultWithFallbackSubscribers.push(callback);

        if (this.lastResponse) {
            callback(this.createFallbackFromResponse(this.lastResponse), this.lastResponse);
        }
    }

    public isQuerySameAsLastOne(query: string): boolean {
        return this.lastUsedQuery === query;
    }

    public isQuerySameAsLastCompletedOne(query: string): boolean {
        return this.lastUsedQueryWhichCompleted === query;
    }

    public abortAllActiveRequests(): void {
        this.activeRequests.forEach((request: Record<string, any>) => {
            request.abort();
        });
        this.activeRequests.clear();
    }

    public updateResultSubscribers(query: string): void {
        const radix = 32;
        const uid = Date.now().toString(radix) + Math.random().toString(radix);
        const request = new FlRequest({
            onSuccess: this.updateSubscribers.bind(this),
            onComplete: () => this.activeRequests.delete(uid),
        });

        query = query.trim();
        const showTopQueries = query === '' ? { autocompleteblocks: ['topQueries', 'product'] } : {};
        let requestObject: RequestParameters = Object.assign(
            {},
            searchForm.getAll(),
            {
                query,
                type: SUPPORTED_API_VERSIONS.includes(config.autocompleteType) ? config.autocompleteType : 'result_v4',
                requestedFilters: '[]',
            },
            showTopQueries
        );

        requestObject = this.addEmptyQuerySort(query, requestObject);

        requestObject = LayoutAssistedSuggestHooks.beforeRequest(query, requestObject);

        requestObject = this.addEmptyQueryFilter(query, requestObject);
        requestObject = this.addOldPriceProperty(requestObject);

        // Abort running requests to avoid race conditions from showing outdated results.
        this.abortAllActiveRequests();

        this.activeRequests.set(uid, request.send('suggest', requestObject));
        this.lastUsedQuery = query;
    }

    public singleSearchRequest(requestObject: RequestParameters, callback: (results: Record<string, any>) => void): void {
        this.singleRequest('search', requestObject, callback);
    }

    public singleNavigationRequest(requestObject: RequestParameters, callback: (results: Record<string, any>) => void): void {
        this.singleRequest('navigation', requestObject, callback);
    }

    public singleSuggestRequest(requestObject: RequestParameters, callback: (results: Record<string, any>) => void): void {
        this.singleRequest('suggest', requestObject, callback);
    }
}
